// Marketing-call consent. No Deno APIs and no network, so Vitest can import it.
//
// Launch is consent-only. An outbound AI marketing call needs a stored record
// that is specifically for phone calls, covers automated AI calls, and names
// the sender. The sender is the client for client campaigns and for managed
// campaigns (Speak places the call, the client still owns the list). It is
// "Speak" only when Speak is calling its own list. No role, flag, or caller
// argument can skip this.
//
// A recorded engagement keeps consent usable after the original date.
// Engagement is one of: an inbound call, an inbound reply, a form submit by
// the person, or an answered call on which they did not opt out. Missed
// calls, voicemail, and opt-outs do not count.

import { assessManagedCampaignDeclaration, type ManagedDeclarationEvidence } from "./clientConsentDeclaration.ts";
import { assessClientVoiceCall, CLIENT_LIST_RECORD_TYPE, CONSENT_RETENTION_POLICY, type ClientListConsent } from "./clientListConsent.ts";
import { canonicalDncPhone, dncNumbersMatch } from "./dncGuard.ts";

export const SPEAK_NAMED_SENDER = "Speak";
export const LAWFUL_BASIS_VERSION = "2026-10-26";
export const LAWFUL_BASIS_STATEMENT =
  "I confirm that this account holds consent, collected by the named sender, for phone calls including automated AI calls to the people we upload. The list is our own customers, or Speak's own contacts when Speak is the sender. It is not a bought, rented, or third-party list.";

export const ENGAGEMENT_KINDS = [
  "inbound_call",
  "inbound_reply",
  "form_submit",
  "answered_call_without_opt_out",
] as const;

export type EngagementKind = (typeof ENGAGEMENT_KINDS)[number];

export const CONSENT_MAX_AGE_MONTHS = 12;

const THIRD_PARTY_TEXT =
  /\b(bought|purchased|rented|rental|third[\s-]?party|3rd[\s-]?party|data[\s-]?broker|appended|cold[\s-]?list|vendor[\s-]?list)\b/i;

export interface StoredConsent {
  userId: string;
  phone: string;
  contactName: string;
  consentedAt: string;
  source: string;
  sourceUrl: string;
  wording: string;
  campaignId?: string | null;
  channel: string;
  coversAiCalls: boolean;
  namedSender: string;
  collectedByNamedSender: boolean;
  listOrigin: string;
  lastEngagedAt?: string | null;
  withdrawnAt?: string | null;
  /** client_list rows belong to campaign_client_consent_records and never authorise Speak. */
  recordKind?: "client_list" | "speak_website";
  /** Visitor IP. Required when the person asked Speak to call them from the website. */
  consentIp?: string | null;
  /**
   * callback: the one requested call, plus service messages about that enquiry.
   * marketing: later marketing calls and texts. Uploaded lists are marketing.
   */
  purpose?: "callback" | "marketing";
  /** phone and/or sms. Marketing calls need phone. Marketing texts need sms. */
  channels?: string[];
}

export interface StoredDeclaration {
  userId: string;
  version: string;
  statement: string;
  listOrigin: string;
  listOriginConfirmed: boolean;
  declaredAt: string;
  declaredBy: string;
}

export interface MarketingCall {
  accountUserId: string;
  phone: string;
  campaignId?: string | null;
  /** Speak calling Speak's own list. Ignored when managedCampaign is set. */
  speakOwnCall?: boolean;
  /** Speak runs the campaign. The named sender stays the client. */
  managedCampaign?: boolean;
  clientName?: string | null;
  /**
   * Set only by POST /calls after it stores a Speak website callback consent.
   * The person asked Speak to call them. This is not an uploaded client list.
   */
  personRequestedCallback?: boolean;
  /**
   * Required when managedCampaign is true. Missing evidence fails closed.
   * The record must match the campaign's current contact-list hash.
   */
  campaignDeclaration?: ManagedDeclarationEvidence | null;
  /**
   * Managed campaigns only. Rows from campaign_client_consent_records.
   * Null or omitted fails closed. marketing_consent_records are not consulted.
   */
  clientListConsent?: Array<ClientListConsent | Record<string, unknown>> | null;
}

export type ConsentReason =
  | "ok"
  | "no_consent"
  | "consent_channel"
  | "consent_not_ai"
  | "consent_sender"
  | "consent_wording"
  | "consent_source"
  | "consent_identity"
  | "consent_time"
  | "consent_stale"
  | "consent_withdrawn"
  | "consent_not_collected_by_sender"
  | "consent_third_party"
  | "consent_scope"
  | "callback_only"
  | "lawful_basis_required"
  | "list_origin_required"
  | "campaign_declaration_required"
  | "client_consent_required"
  | "client_consent_sender"
  | "client_consent_channel"
  | "client_consent_ai_voice"
  | "client_consent_sms"
  | "consent_record_type";

export interface ConsentDecision {
  allowed: boolean;
  reason: ConsentReason;
  message: string;
}

export interface NamedSender {
  senderKind: "client" | "speak";
  namedSender: string | null;
}

export function addCalendarMonths(date: Date, months: number): Date {
  const next = new Date(date.getTime());
  const day = next.getUTCDate();
  next.setUTCMonth(next.getUTCMonth() + months);
  if (next.getUTCDate() < day) next.setUTCDate(0);
  return next;
}

export function isOlderThanTwelveMonths(at: Date, now: Date): boolean {
  return at.getTime() < addCalendarMonths(now, -CONSENT_MAX_AGE_MONTHS).getTime();
}

export function normalizeSender(value: string | null | undefined): string {
  return (value ?? "").trim().replace(/\s+/g, " ").toLowerCase();
}

export function sendersMatch(left: string | null | undefined, right: string | null | undefined): boolean {
  const a = normalizeSender(left);
  const b = normalizeSender(right);
  return a.length > 0 && a === b;
}

export function namedSenderForCall(input: {
  managedCampaign?: boolean;
  speakOwnCall?: boolean;
  clientName?: string | null;
}): NamedSender {
  // Managed campaigns call the client's own list. Staff uploading or launching
  // does not make Speak the sender.
  if (input.managedCampaign || !input.speakOwnCall) {
    const name = input.clientName?.trim().replace(/\s+/g, " ") || "";
    return { senderKind: "client", namedSender: name || null };
  }
  return { senderKind: "speak", namedSender: SPEAK_NAMED_SENDER };
}

export function parseConsentTime(value: string | null | undefined): Date | null {
  if (!value?.trim()) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

export function isThirdPartySignal(value: string | null | undefined): boolean {
  const raw = (value ?? "").trim();
  if (!raw) return false;
  const key = raw.toLowerCase().replace(/[\s-]+/g, "_");
  if (["bought", "purchased", "rented", "rental", "third_party", "thirdparty", "unknown"].includes(key)) {
    return true;
  }
  return THIRD_PARTY_TEXT.test(raw);
}

export function isFirstPartyOrigin(value: string | null | undefined): boolean {
  const key = (value ?? "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  return key === "client_own_customers" || key === "speak_own";
}

export function isPhoneChannel(value: string | null | undefined): boolean {
  return (value ?? "").trim().toLowerCase() === "phone";
}

export function coversAiCalls(value: unknown): boolean {
  if (value === true) return true;
  if (typeof value === "number") return value === 1;
  if (typeof value !== "string") return false;
  const text = value.trim().toLowerCase();
  return text === "true" || text === "yes" || text === "y" || text === "1";
}

export function engagementKindForCall(input: {
  direction?: string | null;
  answered: boolean;
  optedOut: boolean;
}): EngagementKind | null {
  if (!input.answered || input.optedOut) return null;
  if ((input.direction ?? "").toLowerCase() === "inbound") return "inbound_call";
  return "answered_call_without_opt_out";
}

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : value == null ? "" : String(value).trim();
}

export function asStoredConsent(row: Record<string, unknown>): StoredConsent {
  return {
    userId: text(row.userId ?? row.user_id),
    phone: text(row.phone ?? row.phone_number),
    contactName: text(row.contactName ?? row.contact_name),
    consentedAt: text(row.consentedAt ?? row.consented_at),
    source: text(row.source),
    sourceUrl: text(row.sourceUrl ?? row.source_url),
    wording: text(row.wording),
    campaignId: text(row.campaignId ?? row.campaign_id) || null,
    channel: text(row.channel),
    coversAiCalls: coversAiCalls(row.coversAiCalls ?? row.covers_ai_calls),
    namedSender: text(row.namedSender ?? row.named_sender),
    collectedByNamedSender: coversAiCalls(row.collectedByNamedSender ?? row.collected_by_named_sender),
    listOrigin: text(row.listOrigin ?? row.list_origin),
    lastEngagedAt: text(row.lastEngagedAt ?? row.last_engaged_at) || null,
    withdrawnAt: text(row.withdrawnAt ?? row.withdrawn_at) || null,
    consentIp: text(row.consentIp ?? row.consent_ip) || null,
    purpose: consentPurpose(row.purpose ?? row.consent_purpose),
    channels: consentChannels(row.channels, text(row.channel)),
    recordKind: recordKind(row),
  };
}

function recordKind(row: Record<string, unknown>): "client_list" | "speak_website" | undefined {
  const kind = text(row.recordKind ?? row.record_type);
  if (kind === "client_list") return "client_list";
  const source = text(row.source);
  if (source === "speak_website_callback" || source === "speak_website_marketing") return "speak_website";
  return undefined;
}

function consentPurpose(value: unknown): "callback" | "marketing" {
  return text(value) === "callback" ? "callback" : "marketing";
}

function consentChannels(value: unknown, channel: string): string[] {
  const raw = Array.isArray(value) ? value : channel ? [channel] : [];
  const channels = raw.map((item) => text(item).toLowerCase()).filter((item) => item === "phone" || item === "sms");
  return [...new Set(channels)];
}

export function asStoredDeclaration(row: Record<string, unknown>): StoredDeclaration {
  return {
    userId: text(row.userId ?? row.user_id),
    version: text(row.version),
    statement: text(row.statement),
    listOrigin: text(row.listOrigin ?? row.list_origin),
    listOriginConfirmed: coversAiCalls(row.listOriginConfirmed ?? row.list_origin_confirmed),
    declaredAt: text(row.declaredAt ?? row.declared_at),
    declaredBy: text(row.declaredBy ?? row.declared_by),
  };
}

function decision(reason: ConsentReason, message: string, allowed = false): ConsentDecision {
  return { allowed, reason, message };
}

const OK: ConsentDecision = {
  allowed: true,
  reason: "ok",
  message: "",
};

export function assessDeclaration(
  declaration: StoredDeclaration | null,
  accountUserId: string,
): ConsentDecision {
  if (!declaration || declaration.userId !== accountUserId) {
    return decision(
      "lawful_basis_required",
      "This account has not made the lawful-basis declaration, so no number can be called yet.",
    );
  }
  if (
    declaration.version !== LAWFUL_BASIS_VERSION ||
    !declaration.statement ||
    !declaration.declaredAt ||
    !declaration.declaredBy
  ) {
    return decision(
      "lawful_basis_required",
      "The lawful-basis declaration is missing its version, wording, time, or the person who made it.",
    );
  }
  if (!declaration.listOriginConfirmed || !isFirstPartyOrigin(declaration.listOrigin)) {
    return decision(
      "list_origin_required",
      "This account has not confirmed where the contact list came from. Bought, rented, and third-party lists cannot be called.",
    );
  }
  if (isThirdPartySignal(declaration.listOrigin)) {
    return decision(
      "consent_third_party",
      "This account's list origin is bought, rented, or third-party, so no number can be called.",
    );
  }
  return OK;
}

export function campaignLaunchDecision(
  declaration: StoredDeclaration | null,
  accountUserId: string,
): ConsentDecision {
  return assessDeclaration(declaration, accountUserId);
}

function freshnessAnchor(record: StoredConsent): Date | null {
  const consented = parseConsentTime(record.consentedAt);
  if (!consented) return null;
  const engaged = parseConsentTime(record.lastEngagedAt);
  if (engaged && engaged.getTime() > consented.getTime()) return engaged;
  return consented;
}

const REASON_PRIORITY: ConsentReason[] = [
  "consent_withdrawn",
  "consent_third_party",
  "consent_channel",
  "consent_not_ai",
  "consent_sender",
  "consent_not_collected_by_sender",
  "consent_wording",
  "consent_source",
  "consent_identity",
  "consent_time",
  "consent_stale",
  "consent_scope",
  "callback_only",
  "consent_record_type",
  "client_consent_sender",
  "client_consent_channel",
  "client_consent_ai_voice",
  "client_consent_sms",
  "client_consent_required",
  "no_consent",
];

function recordProblem(
  record: StoredConsent,
  call: MarketingCall,
  sender: NamedSender,
  now: Date,
): ConsentReason | null {
  if (record.recordKind === "client_list") return "consent_record_type";
  if (sender.senderKind === "client" && record.recordKind === "speak_website") {
    return record.purpose === "callback" ? "callback_only" : "consent_record_type";
  }
  if (record.withdrawnAt) return "consent_withdrawn";
  if (!isFirstPartyOrigin(record.listOrigin) || isThirdPartySignal(record.listOrigin)) return "consent_third_party";
  const purpose = record.purpose === "callback" ? "callback" : "marketing";
  const channels = record.channels?.length ? record.channels : (isPhoneChannel(record.channel) ? ["phone"] : []);
  if (call.personRequestedCallback) {
    if (purpose !== "callback") return "consent_scope";
  } else if (purpose === "callback") {
    return "callback_only";
  }
  if (!channels.includes("phone") || !isPhoneChannel(record.channel)) return "consent_channel";
  if (!record.coversAiCalls) return "consent_not_ai";
  if (!sender.namedSender || !sendersMatch(record.namedSender, sender.namedSender)) return "consent_sender";
  if (sender.senderKind === "client" && normalizeSender(record.namedSender) === normalizeSender(SPEAK_NAMED_SENDER)) {
    return "consent_sender";
  }
  if (sender.senderKind === "speak" && record.listOrigin !== "speak_own") return "consent_sender";
  if (sender.senderKind === "client" && record.listOrigin !== "client_own_customers") return "consent_sender";
  if (!record.collectedByNamedSender) return "consent_not_collected_by_sender";
  if (!record.wording) return "consent_wording";
  if (!record.source || !record.sourceUrl) return "consent_source";
  if (!record.contactName || !record.phone) return "consent_identity";
  if (!parseConsentTime(record.consentedAt)) return "consent_time";
  const anchor = freshnessAnchor(record);
  if (!anchor || isOlderThanTwelveMonths(anchor, now)) return "consent_stale";
  if (record.campaignId && record.campaignId !== (call.campaignId ?? "")) return "consent_scope";
  return null;
}

function messageFor(reason: ConsentReason, sender: NamedSender): string {
  switch (reason) {
    case "consent_withdrawn":
      return "This person has withdrawn consent, so the number was not called.";
    case "consent_third_party":
      return "Consent for this number is not from the named sender's own list. Bought, rented, and third-party lists cannot be called.";
    case "consent_channel":
      return "SMS, email, or a general contact-me tick does not allow an AI phone call. Consent has to be for phone calls.";
    case "consent_not_ai":
      return "Consent for this number does not cover automated AI calls.";
    case "consent_sender":
      return sender.senderKind === "speak"
        ? "This call is from Speak, but the consent record does not name Speak."
        : `This call is for ${sender.namedSender || "the client"}. The consent record must name that client, including when Speak staff upload or launch a managed campaign.`;
    case "consent_not_collected_by_sender":
      return "Consent has to have been collected by the named sender.";
    case "consent_wording":
      return "The consent record is missing the exact wording that was shown.";
    case "consent_source":
      return "The consent record is missing the source URL or form, or the source name.";
    case "consent_identity":
      return "The consent record must name who consented and the phone number.";
    case "consent_time":
      return "The consent record is missing the date and time of consent.";
    case "consent_stale":
      return "Consent is older than 12 months and there is no recorded engagement since, so the number was not called.";
    case "consent_scope":
      return "This consent record applies to a different campaign.";
    case "callback_only":
      return "This consent only covers the call they requested and service messages about that enquiry. It does not allow a marketing call or text.";
    case "no_consent":
      return "This number has no consent record, so it was not called.";
    case "consent_record_type":
      return sender.senderKind === "speak"
        ? "A client list consent record cannot authorise a Speak marketing call."
        : "A Speak website consent record cannot authorise a client campaign.";
    default:
      return "This number cannot be called.";
  }
}

export function assessMarketingCall(input: {
  records: Array<StoredConsent | Record<string, unknown>>;
  declaration: StoredDeclaration | Record<string, unknown> | null;
  call: MarketingCall;
  now: Date;
}): ConsentDecision {
  const callback = input.call.personRequestedCallback === true;
  const call = callback
    ? { ...input.call, speakOwnCall: true, managedCampaign: false, clientName: SPEAK_NAMED_SENDER }
    : input.call;
  const declaration = input.declaration
    ? asStoredDeclaration(input.declaration as Record<string, unknown>)
    : null;
  if (call.managedCampaign) {
    const campaignDeclaration = assessManagedCampaignDeclaration({
      accountUserId: call.accountUserId,
      campaignId: call.campaignId,
      evidence: call.campaignDeclaration,
    });
    if (campaignDeclaration.allowed === false) {
      return decision("campaign_declaration_required", campaignDeclaration.message);
    }
    if (!callback) {
      const declared = assessDeclaration(declaration, call.accountUserId);
      if (!declared.allowed) return declared;
    }
    const clientConsent = assessClientVoiceCall({
      records: call.clientListConsent,
      phone: call.phone,
      accountUserId: call.accountUserId,
      campaignId: call.campaignId,
      contactListId: call.campaignDeclaration?.currentListId,
      declarationSender: call.campaignDeclaration?.record?.clientLegalName,
    });
    if (clientConsent.allowed) return OK;
    return decision(clientConsent.reason, clientConsent.message);
  }
  if (!callback) {
    const declared = assessDeclaration(declaration, call.accountUserId);
    if (!declared.allowed) return declared;
  }

  const sender = namedSenderForCall(call);
  if (!sender.namedSender) {
    return decision(
      "consent_sender",
      "The client name is missing, so the call cannot be matched to a named sender.",
    );
  }

  const records = input.records.map((row) => asStoredConsent(row as Record<string, unknown>));
  const sameAccount = records.filter((record) =>
    record.userId === call.accountUserId && dncNumbersMatch(record.phone, call.phone)
  );
  if (sameAccount.length === 0) {
    return decision("no_consent", messageFor("no_consent", sender));
  }

  const problems: ConsentReason[] = [];
  for (const record of sameAccount) {
    const problem = recordProblem(record, call, sender, input.now);
    if (!problem) {
      if (callback && !record.consentIp) {
        return decision("consent_source", "The website callback is missing the visitor IP, so the call was not placed.");
      }
      return OK;
    }
    problems.push(problem);
  }
  const reason = REASON_PRIORITY.find((item) => problems.includes(item)) ?? "no_consent";
  return decision(reason, messageFor(reason, sender));
}

export interface UploadAttestation {
  confirmedFirstParty: boolean;
  notThirdParty: boolean;
  listOrigin: string;
}

export interface UploadListLevel {
  source?: string | null;
  sourceUrl?: string | null;
  wording?: string | null;
  consentedAt?: string | null;
  channel?: string | null;
  coversAiCalls?: unknown;
}

export interface UploadRow {
  rowNumber: number;
  phone?: string | null;
  contactName?: string | null;
  companyName?: string | null;
  email?: string | null;
  industry?: string | null;
  companySize?: string | null;
  website?: string | null;
  notes?: string | null;
  consentedAt?: string | null;
  source?: string | null;
  sourceUrl?: string | null;
  wording?: string | null;
  channel?: string | null;
  coversAiCalls?: unknown;
  namedSender?: string | null;
  collectedByNamedSender?: unknown;
  listOrigin?: string | null;
  campaignId?: string | null;
  channels?: string[] | string | null;
  softOptIn?: unknown;
}

export interface UploadRequest {
  accountUserId: string;
  actorUserId: string;
  clientName?: string | null;
  speakOwnCall?: boolean;
  managedCampaign?: boolean;
  campaignId?: string | null;
  /** Managed campaigns only. The campaign_contact_lists row this file was stored as. */
  contactListId?: string | null;
  sourcePath: string;
  attestation: UploadAttestation | null;
  listLevel?: UploadListLevel | null;
  rows: UploadRow[];
}

export interface UploadRejection {
  rowNumber: number;
  phone: string | null;
  reasons: string[];
}

export interface AcceptedContact {
  rowNumber: number;
  lead: Record<string, unknown>;
  consent: StoredConsent;
}

export interface UploadAssessment {
  accepted: AcceptedContact[];
  rejections: UploadRejection[];
  /** The whole list was refused, not only individual rows. */
  blockedList: boolean;
}

function firstText(...values: Array<string | null | undefined>): string {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) return trimmed;
  }
  return "";
}

function hasUploadValue(value: unknown): boolean {
  if (typeof value === "string") return value.trim() !== "";
  return value != null;
}

function explicitFalse(value: unknown): boolean {
  if (value === false) return true;
  if (typeof value !== "string") return false;
  const textValue = value.trim().toLowerCase();
  return textValue === "false" || textValue === "no" || textValue === "n" || textValue === "0";
}

export function assessContactUpload(request: UploadRequest, now: Date = new Date()): UploadAssessment {
  const sender = namedSenderForCall(request);
  const rows = request.rows.length
    ? request.rows
    : [{ rowNumber: 0, phone: null } satisfies UploadRow];
  const rejectAll = (reason: string): UploadAssessment => ({
    accepted: [],
    blockedList: true,
    rejections: rows.map((row) => ({
      rowNumber: row.rowNumber,
      phone: row.phone ?? null,
      reasons: [reason],
    })),
  });

  const attestation = request.attestation;
  if (!attestation?.confirmedFirstParty || !attestation.notThirdParty) {
    return rejectAll(
      "Confirm that this list was collected by the named sender and is not a bought, rented, or third-party list.",
    );
  }
  if (!isFirstPartyOrigin(attestation.listOrigin) || isThirdPartySignal(attestation.listOrigin)) {
    return rejectAll(
      "This upload is marked as bought, rented, or third-party. Those lists cannot be imported.",
    );
  }
  const expectedOrigin = sender.senderKind === "speak" ? "speak_own" : "client_own_customers";
  if (attestation.listOrigin.trim().toLowerCase().replace(/[\s-]+/g, "_") !== expectedOrigin) {
    return rejectAll(
      sender.senderKind === "client"
        ? "A client or managed campaign has to be marked as the client's own customers. Speak is not the named sender."
        : "Speak's own calls have to be marked as Speak's own list.",
    );
  }
  if (!sender.namedSender) {
    return rejectAll("The client name is missing, so this list cannot name the sender. Add the client's company name and try again.");
  }

  const accepted: AcceptedContact[] = [];
  const rejections: UploadRejection[] = [];
  const list = request.listLevel ?? {};

  for (const row of rows) {
    const reasons: string[] = [];
    const phone = canonicalDncPhone(row.phone) ?? "";
    const contactName = firstText(row.contactName);
    const companyName = firstText(row.companyName);
    const source = firstText(row.source, list.source);
    const sourceUrl = firstText(row.sourceUrl, list.sourceUrl);
    const wording = firstText(row.wording, list.wording);
    const consentedAt = firstText(row.consentedAt, list.consentedAt);
    const channel = firstText(row.channel, list.channel);
    const namedSender = firstText(row.namedSender, sender.namedSender);
    const listOrigin = firstText(row.listOrigin, attestation.listOrigin);
    const aiValue = hasUploadValue(row.coversAiCalls) ? row.coversAiCalls : list.coversAiCalls;
    const collectedValue = row.collectedByNamedSender;

    if (!companyName) reasons.push("Company name is required.");
    if (!phone) reasons.push("A valid phone number is required.");
    if (!contactName) reasons.push("The name of the person who consented is required.");
    if (!parseConsentTime(consentedAt)) reasons.push("The date and time of consent are required.");
    if (!source) reasons.push("A consent source is required on the row or for the list as a whole.");
    if (!sourceUrl) reasons.push("The source URL or form is required on the row or for the list as a whole.");
    if (!wording) reasons.push("The exact consent wording that was shown is required on the row or for the list as a whole.");
    if (!isPhoneChannel(channel)) {
      reasons.push("SMS, email, or a general contact-me tick does not count. Channel has to be phone.");
    }
    if (!coversAiCalls(aiValue)) reasons.push("Consent has to cover automated AI calls.");
    if (!sendersMatch(namedSender, sender.namedSender)) {
      reasons.push(
        sender.senderKind === "client"
          ? `The named sender must be ${sender.namedSender}. Managed campaigns name the client, not Speak.`
          : "The named sender must be Speak.",
      );
    }
    if (explicitFalse(collectedValue)) reasons.push("Consent has to have been collected by the named sender.");
    if (!isFirstPartyOrigin(listOrigin) || isThirdPartySignal(listOrigin) || isThirdPartySignal(source) || isThirdPartySignal(wording)) {
      reasons.push("This row is marked or detected as a bought, rented, or third-party list.");
    }
    const consented = parseConsentTime(consentedAt);
    if (consented && isOlderThanTwelveMonths(consented, now)) {
      reasons.push("Consent is older than 12 months. An upload cannot refresh it without a recorded engagement.");
    }

    if (reasons.length) {
      rejections.push({ rowNumber: row.rowNumber, phone: row.phone ?? null, reasons });
      continue;
    }

    accepted.push({
      rowNumber: row.rowNumber,
      lead: {
        company_name: companyName,
        contact_name: contactName,
        phone,
        email: firstText(row.email) || null,
        industry: firstText(row.industry) || null,
        company_size: firstText(row.companySize) || null,
        website: firstText(row.website) || null,
        notes: firstText(row.notes) || null,
        campaign_id: row.campaignId ?? request.campaignId ?? null,
        call_status: "pending",
        user_id: request.accountUserId,
      },
      consent: {
        userId: request.accountUserId,
        phone,
        contactName,
        consentedAt: consented!.toISOString(),
        source,
        sourceUrl,
        wording,
        campaignId: row.campaignId ?? request.campaignId ?? null,
        channel: "phone",
        channels: ["phone"],
        purpose: "marketing",
        coversAiCalls: true,
        namedSender: sender.namedSender!,
        collectedByNamedSender: true,
        listOrigin: expectedOrigin,
        lastEngagedAt: null,
        withdrawnAt: null,
      },
    });
  }

  return { accepted, rejections, blockedList: false };
}

export interface ManagedClientUploadAssessment {
  accepted: Array<{ rowNumber: number; lead: Record<string, unknown>; clientConsent: Record<string, unknown> }>;
  rejections: UploadRejection[];
  blockedList: boolean;
}

function declaredChannels(value: unknown, channel: string): string[] {
  if (Array.isArray(value)) return consentChannels(value, "");
  const raw = firstText(typeof value === "string" ? value : "", channel).toLowerCase();
  if (!raw) return [];
  if (raw === "both" || raw === "phone,sms" || raw === "phone and sms" || raw === "ai_voice,sms") return ["phone", "sms"];
  if (raw === "sms" || raw === "text" || raw === "texts") return ["sms"];
  if (raw === "phone" || raw === "voice" || raw === "call" || raw === "calls" || raw === "ai_voice") return ["phone"];
  if (raw.includes("phone") && raw.includes("sms")) return ["phone", "sms"];
  return consentChannels([raw], "");
}

/**
 * Managed-campaign import. Writes client_list rows, not Speak website consent.
 * SMS-only rows are stored. They do not become permission to dial.
 */
export function assessManagedClientUpload(request: UploadRequest, contactListId: string, now: Date = new Date()): ManagedClientUploadAssessment {
  const senderName = namedSenderForCall(request);
  const rows = request.rows.length ? request.rows : [{ rowNumber: 0, phone: null } satisfies UploadRow];
  const rejectAll = (reason: string): ManagedClientUploadAssessment => ({
    accepted: [],
    blockedList: true,
    rejections: rows.map((row) => ({ rowNumber: row.rowNumber, phone: row.phone ?? null, reasons: [reason] })),
  });
  const attestation = request.attestation;
  if (!attestation?.confirmedFirstParty || !attestation.notThirdParty) {
    return rejectAll("Confirm that this list was collected by the client and is not a bought, rented, or third-party list.");
  }
  if (!isFirstPartyOrigin(attestation.listOrigin) || isThirdPartySignal(attestation.listOrigin)) {
    return rejectAll("This upload is marked as bought, rented, or third-party. Those lists cannot be imported.");
  }
  if (!request.campaignId || !contactListId.trim()) {
    return rejectAll("Record the contact list file on this campaign before importing contacts.");
  }
  if (!senderName.namedSender || normalizeSender(senderName.namedSender) === normalizeSender(SPEAK_NAMED_SENDER)) {
    return rejectAll("The client legal name is missing, so this list cannot name the sender.");
  }

  const accepted: ManagedClientUploadAssessment["accepted"] = [];
  const rejections: UploadRejection[] = [];
  const list = request.listLevel ?? {};
  for (const row of rows) {
    const reasons: string[] = [];
    const phone = canonicalDncPhone(row.phone) ?? "";
    const contactName = firstText(row.contactName);
    const companyName = firstText(row.companyName);
    const source = firstText(row.source, list.source);
    const wording = firstText(row.wording, list.wording);
    const consentedAt = firstText(row.consentedAt, list.consentedAt);
    const sender = firstText(row.namedSender, senderName.namedSender);
    const channels = declaredChannels(row.channels, firstText(row.channel, list.channel));
    const aiValue = hasUploadValue(row.coversAiCalls) ? row.coversAiCalls : list.coversAiCalls;
    const aiVoice = hasUploadValue(row.coversAiCalls) || hasUploadValue(list.coversAiCalls) ? coversAiCalls(aiValue) : false;
    if (!companyName) reasons.push("Company name is required.");
    if (!phone) reasons.push("A valid phone number is required.");
    if (!contactName) reasons.push("The name of the person who consented is required.");
    if (!parseConsentTime(consentedAt)) reasons.push("The date and time of consent are required.");
    if (!source) reasons.push("A consent source is required on the row or for the list as a whole.");
    if (!wording) reasons.push("The exact consent wording that was shown is required on the row or for the list as a whole.");
    if (!channels.length) reasons.push("Channels must include phone, sms, or both.");
    if (!sender || normalizeSender(sender) === normalizeSender(SPEAK_NAMED_SENDER)) {
      reasons.push("The sender must be the client legal name, not Speak.");
    }
    if (isThirdPartySignal(source) || isThirdPartySignal(wording)) {
      reasons.push("This row is marked or detected as a bought, rented, or third-party list.");
    }
    const consented = parseConsentTime(consentedAt);
    if (consented && isOlderThanTwelveMonths(consented, now)) {
      reasons.push("Consent is older than 12 months. An upload cannot refresh it without a recorded engagement.");
    }
    if (reasons.length) {
      rejections.push({ rowNumber: row.rowNumber, phone: row.phone ?? null, reasons });
      continue;
    }
    accepted.push({
      rowNumber: row.rowNumber,
      lead: {
        company_name: companyName,
        contact_name: contactName,
        phone,
        email: firstText(row.email) || null,
        industry: firstText(row.industry) || null,
        company_size: firstText(row.companySize) || null,
        website: firstText(row.website) || null,
        notes: firstText(row.notes) || null,
        campaign_id: row.campaignId ?? request.campaignId ?? null,
        call_status: "pending",
        user_id: request.accountUserId,
      },
      clientConsent: {
        user_id: request.accountUserId,
        campaign_id: request.campaignId,
        contact_list_id: contactListId,
        phone_number: phone,
        sender,
        channels,
        ai_voice: aiVoice,
        source,
        consented_at: consented!.toISOString(),
        wording,
        soft_opt_in: flagTrue(row.softOptIn),
        record_type: CLIENT_LIST_RECORD_TYPE,
        retention_policy: CONSENT_RETENTION_POLICY,
        created_by: request.actorUserId,
      },
    });
  }
  return { accepted, rejections, blockedList: false };
}

function flagTrue(value: unknown): boolean {
  return value === true || (typeof value === "string" && ["true", "yes", "y", "1"].includes(value.trim().toLowerCase()));
}

export function consentInsertRow(consent: StoredConsent, actorUserId: string): Record<string, unknown> {
  return {
    user_id: consent.userId,
    phone_number: consent.phone,
    contact_name: consent.contactName,
    consented_at: consent.consentedAt,
    source: consent.source,
    source_url: consent.sourceUrl,
    wording: consent.wording,
    campaign_id: consent.campaignId ?? null,
    channel: consent.channel,
    covers_ai_calls: consent.coversAiCalls,
    named_sender: consent.namedSender,
    collected_by_named_sender: consent.collectedByNamedSender,
    list_origin: consent.listOrigin,
    last_engaged_at: consent.lastEngagedAt ?? null,
    withdrawn_at: null,
    created_by: actorUserId,
    consent_purpose: consent.purpose === "callback" ? "callback" : "marketing",
    channels: consent.channels?.length ? consent.channels : ["phone"],
    ...(consent.consentIp ? { consent_ip: consent.consentIp } : {}),
  };
}

export const SPEAK_WEBSITE_CALLBACK_SOURCE = "speak_website_callback";
export const SPEAK_WEBSITE_CALLBACK_VARIABLE = "website_widget";

export const SPEAK_WEBSITE_MARKETING_SOURCE = "speak_website_marketing";

export interface SpeakCallbackInput {
  phone: string | null;
  contactName: string | null;
  variableSource: string | null;
  callPurpose: string | null;
  consent: unknown;
  marketingConsent?: unknown;
}

export type MarketingConsentStore =
  | { stored: false; reason: string }
  | { stored: true; record: StoredConsent };

export type SpeakCallbackParse =
  | { applicable: false }
  | { applicable: true; ok: false; missing: string[] }
  | { applicable: true; ok: true; record: StoredConsent; marketing: MarketingConsentStore };

export function isSpeakWebsiteCallback(variableSource: string | null, callPurpose: string | null): boolean {
  if ((variableSource ?? "").trim().toLowerCase() === SPEAK_WEBSITE_CALLBACK_VARIABLE) return true;
  return /website callback/i.test(callPurpose ?? "");
}

/**
 * speak.ad posts to POST /calls. The exact form sentence lives on the website,
 * so the request has to carry it. NativePhil/speak-ws is not in this repo.
 */
export function parseSpeakWebsiteCallback(input: SpeakCallbackInput, accountUserId: string): SpeakCallbackParse {
  if (!isSpeakWebsiteCallback(input.variableSource, input.callPurpose)) return { applicable: false };
  const consent = input.consent && typeof input.consent === "object" && !Array.isArray(input.consent)
    ? input.consent as Record<string, unknown>
    : {};
  const missing: string[] = [];
  const wording = text(consent.wording);
  const sourceUrl = text(consent.source_url ?? consent.sourceUrl);
  const consentedAtRaw = text(consent.consented_at ?? consent.consentedAt);
  const ip = text(consent.ip);
  const name = text(input.contactName);
  const phone = canonicalDncPhone(input.phone) ?? "";
  const channel = text(consent.channel) || "phone";
  const covers = consent.covers_ai_calls ?? consent.coversAiCalls;
  const confirmed = consent.confirmed === true || consent.requested_call === true || consent.requestedCall === true;

  if (!confirmed) missing.push("consent.confirmed must be true. The person has to ask Speak to call them.");
  if (!name) missing.push("name is required so the record shows who consented.");
  if (!phone) missing.push("A valid phone number is required.");
  if (wording.length < 20) missing.push("consent.wording must be the exact sentence shown on the speak.ad form.");
  if (!/^https?:\/\//i.test(sourceUrl)) missing.push("consent.source_url must be the https page URL where that sentence was shown.");
  if (!parseConsentTime(consentedAtRaw)) missing.push("consent.consented_at must be the ISO time the person ticked the form.");
  if (ip.length < 3) missing.push("consent.ip must be the visitor IP address.");
  if (!isPhoneChannel(channel)) missing.push("consent.channel must be phone. SMS or email consent does not allow the call.");
  if (!coversAiCalls(covers)) missing.push("consent.covers_ai_calls must be true. The form has to cover an automated AI call.");
  if (missing.length) return { applicable: true, ok: false, missing };

  const consentedAt = parseConsentTime(consentedAtRaw)!.toISOString();
  return {
    applicable: true,
    ok: true,
    record: {
      userId: accountUserId,
      phone,
      contactName: name,
      consentedAt,
      source: SPEAK_WEBSITE_CALLBACK_SOURCE,
      sourceUrl,
      wording,
      campaignId: null,
      channel: "phone",
      channels: ["phone"],
      purpose: "callback",
      coversAiCalls: true,
      namedSender: SPEAK_NAMED_SENDER,
      collectedByNamedSender: true,
      listOrigin: "speak_own",
      lastEngagedAt: null,
      withdrawnAt: null,
      consentIp: ip,
    },
    marketing: parseSpeakMarketingConsent(input.marketingConsent, {
      accountUserId,
      phone,
      contactName: name,
    }),
  };
}

/**
 * Optional second tick. Same facts as `consent`, except `channels` is an array
 * such as ["phone","sms"]. Absent or invalid marketing consent is not stored
 * and does not block the callback the person just requested.
 */
export function parseSpeakMarketingConsent(
  raw: unknown,
  identity: { accountUserId: string; phone: string; contactName: string },
): MarketingConsentStore {
  if (raw == null) return { stored: false, reason: "not_provided" };
  if (typeof raw !== "object" || Array.isArray(raw)) {
    return { stored: false, reason: "marketing_consent must be an object." };
  }
  const consent = raw as Record<string, unknown>;
  const missing: string[] = [];
  const wording = text(consent.wording);
  const sourceUrl = text(consent.source_url ?? consent.sourceUrl);
  const consentedAtRaw = text(consent.consented_at ?? consent.consentedAt);
  const ip = text(consent.ip);
  const covers = consent.covers_ai_calls ?? consent.coversAiCalls;
  const confirmed = consent.confirmed === true || consent.requested_call === true || consent.requestedCall === true;
  const channels = consentChannels(consent.channels, "");

  if (!confirmed) missing.push("marketing_consent.confirmed must be true.");
  if (wording.length < 20) missing.push("marketing_consent.wording must be the exact marketing sentence shown on the form.");
  if (!/^https?:\/\//i.test(sourceUrl)) missing.push("marketing_consent.source_url must be the https page URL where that sentence was shown.");
  if (!parseConsentTime(consentedAtRaw)) missing.push("marketing_consent.consented_at must be the ISO time the person ticked marketing consent.");
  if (ip.length < 3) missing.push("marketing_consent.ip must be the visitor IP address.");
  if (!coversAiCalls(covers)) missing.push("marketing_consent.covers_ai_calls must be true.");
  if (!channels.length) missing.push("marketing_consent.channels must include phone, sms, or both.");
  if (Array.isArray(consent.channels) && consent.channels.some((item) => !["phone", "sms"].includes(text(item).toLowerCase()))) {
    missing.push("marketing_consent.channels can only include phone and sms.");
  }
  if (!identity.phone || !identity.contactName) missing.push("name and phone are required.");
  if (missing.length) return { stored: false, reason: missing.join(" ") };

  const includesPhone = channels.includes("phone");
  return {
    stored: true,
    record: {
      userId: identity.accountUserId,
      phone: identity.phone,
      contactName: identity.contactName,
      consentedAt: parseConsentTime(consentedAtRaw)!.toISOString(),
      source: SPEAK_WEBSITE_MARKETING_SOURCE,
      sourceUrl,
      wording,
      campaignId: null,
      channel: includesPhone ? "phone" : "sms",
      channels,
      purpose: "marketing",
      coversAiCalls: true,
      namedSender: SPEAK_NAMED_SENDER,
      collectedByNamedSender: true,
      listOrigin: "speak_own",
      lastEngagedAt: null,
      withdrawnAt: null,
      consentIp: ip,
    },
  };
}
